import { cn } from '@reelplicate/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

/**
 * shadcn Button, restyled to the Figma "Button" and "Icon Button" component
 * sets (page ↳ Button). Owned code: variants and sizes are the Figma ones, not
 * shadcn's. Apps use the wrappers in components/lib (Button, IconButton),
 * which add the Figma anatomy - label wrap, icons, loading.
 */
const buttonVariants = cva(
  [
    'relative inline-flex shrink-0 items-center justify-center overflow-clip whitespace-nowrap outline-none select-none',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    // Motion (Figma): colour 120ms ease-out; press scale 0.97 over 100ms;
    // reduced motion keeps colour, drops scale.
    'transition-[color,background-color,border-color,box-shadow,scale] duration-[120ms,120ms,120ms,120ms,100ms] ease-out',
    'active:scale-[0.97] motion-reduce:active:scale-100',
    // Focus/Ring effect style.
    'focus-visible:shadow-focus-ring',
    'disabled:pointer-events-none aria-disabled:pointer-events-none',
    // Open (Figma Popover / Menu examples): a trigger whose popup is open
    // (Radix data-state=open, or aria-expanded=true) keeps the Pressed look
    // of its style - see the secondary / outline / ghost variants.
  ],
  {
    variants: {
      variant: {
        primary:
          'bg-brand-primary text-text-on-brand hover:bg-brand-primary-hover active:bg-brand-primary-pressed',
        secondary:
          'bg-bg-inverse text-text-inverse hover:bg-bg-inverse-hover active:bg-bg-inverse-pressed data-[state=open]:bg-bg-inverse-pressed aria-expanded:bg-bg-inverse-pressed',
        outline:
          'border border-border-strong bg-bg-surface text-text-primary hover:border-border-strong-hover hover:bg-bg-subtle active:border-border-strong-hover active:bg-bg-muted data-[state=open]:border-border-strong-hover data-[state=open]:bg-bg-muted aria-expanded:border-border-strong-hover aria-expanded:bg-bg-muted',
        // Ghost focus is a plain 2px border/focus outline, no surface gap.
        ghost:
          'text-text-primary hover:bg-bg-muted active:bg-bg-emphasis data-[state=open]:bg-bg-emphasis aria-expanded:bg-bg-emphasis focus-visible:shadow-none focus-visible:ring-2 focus-visible:ring-border-focus',
        danger:
          'bg-status-danger-solid text-text-inverse hover:bg-status-danger-solid-hover active:bg-status-danger-solid-pressed',
      },
      size: {
        // Label/Medium · 32 · padding 8 (+4 label wrap) · gap 2 · icon 16
        sm: 'text-label-md h-8 gap-0.5 rounded-md px-2 [&_svg]:size-4',
        // Label/Medium · 40 · padding 12 (+4) · gap 4 · icon 18 (default)
        md: 'text-label-md h-10 gap-1 rounded-md px-3 [&_svg]:size-[18px]',
        // Label/Large · 48 · padding 16 (+4) · gap 4 · icon 20 · radius lg
        lg: 'text-label-lg h-12 gap-1 rounded-lg px-4 [&_svg]:size-5',
        // Icon Button: square, icon 16 / 20 / 24.
        'icon-sm': 'size-8 rounded-md [&_svg]:size-4',
        'icon-md': 'size-10 rounded-md [&_svg]:size-5',
        'icon-lg': 'size-12 rounded-lg [&_svg]:size-6',
      },
    },
    compoundVariants: [
      // Disabled: every style drops to bg/disabled + text/disabled, except
      // Outline (surface + border/subtle) and Ghost (no fill). Loading is also
      // disabled but keeps its normal look (aria-busy), per Figma.
      {
        variant: ['primary', 'secondary', 'danger'],
        className:
          'disabled:not-aria-busy:bg-bg-disabled disabled:not-aria-busy:text-text-disabled aria-disabled:not-aria-busy:bg-bg-disabled aria-disabled:not-aria-busy:text-text-disabled',
      },
      {
        variant: 'outline',
        className:
          'disabled:not-aria-busy:border-border-subtle disabled:not-aria-busy:bg-bg-surface disabled:not-aria-busy:text-text-disabled aria-disabled:not-aria-busy:border-border-subtle aria-disabled:not-aria-busy:bg-bg-surface aria-disabled:not-aria-busy:text-text-disabled',
      },
      {
        variant: 'ghost',
        className:
          'disabled:not-aria-busy:text-text-disabled aria-disabled:not-aria-busy:text-text-disabled',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

function Button({
  className,
  variant = 'primary',
  size = 'md',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
